import { Inject, Injectable } from '@angular/core';
import type { AumicoAnnualReportConfig } from '../aumico-annualreport.config';
import { AumicoAnnualReportConfigService } from '../aumico-annualreport.config';

@Injectable({
    providedIn: 'root',
})
export class RoutesUtil {
    private readonly microServiceName = 'annualreport';
    private readonly annualReportsTemplates = 'annual-report-templates';
    private readonly route = '/';
    private readonly textSnippet = 'text-snippet';
    private readonly textSnippetsList = 'text-snippets-list';
    private readonly delete = 'delete';
    private readonly update = 'update';
    private readonly reporting = 'reporting';
    private readonly reportingTemplates = 'reporting-templates';
    private readonly default = 'default';
    private readonly defaultHgb = 'default-hgb';
    private readonly pdf = 'pdf';
    private readonly pdfV2 = 'pdf/v2';
    // REV-21106: sync HTML → PDF route. The FE ships a self-contained HTML
    // document (currently used by the reclassifications-preview "Export
    // PDF" button); the BE forwards it to Gotenberg and streams the PDF
    // back in the same response. No Pub/Sub round-trip, no template.
    private readonly htmlPdf = 'html/pdf';
    private readonly word = 'docx';
    private readonly wordV2 = 'docx/v2';
    private readonly excel = 'xlsx';
    private readonly excelV2 = 'xlsx/v2';
    private readonly excelStructuredV2 = 'xlsx/structured/v2';
    private readonly shares = 'shares';
    private readonly acceptance = 'acceptance';
    private readonly rejection = 'rejection';
    private readonly shareInformation = 'shareInformation';
    private readonly server: string;

    constructor(@Inject(AumicoAnnualReportConfigService) private readonly config: AumicoAnnualReportConfig) {
        this.server = this.config.apiGateway;
    }

    public getTextSnippetRoute(): string {
        return `${this.server}${this.microServiceName}${this.route}${this.textSnippet}`;
    }

    public getAllTextSnippetsRoute(): string {
        return `${this.server}${this.microServiceName}${this.route}${this.textSnippetsList}`;
    }

    public deleteTextSnippetsRoute(): string {
        return `${this.server}${this.microServiceName}${this.route}${this.textSnippetsList}${this.route}${this.delete}`;
    }

    public updateTextSnippetsRoute(id: string): string {
        return `${this.server}${this.microServiceName}${this.route}${this.textSnippet}${this.route}${this.update}${this.route}${id}`;
    }

    public getTextSnippetByIdRoute(id: string): string {
        return `${this.server}${this.microServiceName}${this.route}${this.textSnippet}${this.route}${id}`;
    }

    public reportingPdf(): string {
        return `${this.server}${this.reporting}${this.route}${this.pdf}`;
    }

    public reportingPdfV2(): string {
        return `${this.server}${this.reporting}${this.route}${this.pdfV2}`;
    }

    public reportingHtmlPdf(): string {
        return `${this.server}${this.reporting}${this.route}${this.htmlPdf}`;
    }

    public exportToWord(): string {
        return `${this.server}${this.reporting}${this.route}${this.word}`;
    }

    public exportToWordV2(): string {
        return `${this.server}${this.reporting}${this.route}${this.wordV2}`;
    }

    public exportExcel(): string {
        return `${this.server}${this.reporting}${this.route}${this.excel}`;
    }

    public exportExcelV2(): string {
        return `${this.server}${this.reporting}${this.route}${this.excelV2}`;
    }
    
    public exportExcelStructured(): string {
        return `${this.server}${this.reporting}${this.route}${this.excel}/structured`;
    }

    public exportExcelStructuredV2(): string {
        return `${this.server}${this.reporting}${this.route}${this.excelStructuredV2}`;
    }

    public createArTemplate(): string {
        return `${this.server}${this.annualReportsTemplates}`;
    }

    public getArTemplateById(id: string): string {
        return `${this.server}${this.annualReportsTemplates}${this.route}${id}`;
    }

    public updateArTemplateById(id: string): string {
        return `${this.server}${this.annualReportsTemplates}${this.route}${id}`;
    }

    public getAllArTemplates(): string {
        return `${this.server}${this.annualReportsTemplates}`;
    }

    public deleteArTemplate(id: string): string {
        return `${this.server}${this.annualReportsTemplates}${this.route}${id}`;
    }

    public downloadDefaultPdfTemplate(): string {
        return `${this.server}${this.reportingTemplates}${this.route}${this.default}`;
    }

    public downloadDefaultHgbPdfTemplate(): string {
        return `${this.server}${this.reportingTemplates}${this.route}${this.defaultHgb}`;
    }

    public downloadPdfTemplate(id: string): string {
        return `${this.server}${this.reportingTemplates}${this.route}${id}`;
    }

    public uploadPdfTemplate(): string {
        return `${this.server}${this.reportingTemplates}`;
    }
    public shareAnnualReport(): string {
        return `${this.server}${this.shares}`;
    }
    public getCurrentActiveUserSharedAnnualReportsUrl(): string {
        return `${this.server}${this.shares}`;
    }
    public acceptCurrentActiveUserSharedAnnualReportsUrl(id: string): string {
        return `${this.server}${this.shares}${this.route}${id}${this.route}${this.acceptance}`;
    }
    public declineCurrentActiveUserSharedAnnualReportsUrl(id: string): string {
        return `${this.server}${this.shares}${this.route}${id}${this.route}${this.rejection}`;
    }
    public getShareInformation(id: string): string {
        return `${this.server}${this.microServiceName}${this.route}${this.shareInformation}${this.route}${id}`;
    }

    public getArTemplateLocales(id: string): string {
        return `${this.server}${this.annualReportsTemplates}${this.route}${id}${this.route}locales`;
    }

    public getArTemplatesTranslations(): string {
        return `${this.server}${this.annualReportsTemplates}${this.route}translations`;
    }
}
